"use client"

import { useState, useRef } from "react"
import { ChevronRight, Search, MessageSquare, HelpCircle, AlertCircle, Bookmark, ExternalLink } from "lucide-react"
import { Button } from "@/components/ui/button"

export function SolveRealProblems() {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0)
  const cardRef = useRef<HTMLDivElement>(null)

  const questions = [
    {
      id: 1,
      question: "What is the difference between a 245D license and a Home Care license?",
      answer:
        "The differences between a 245D license and a Home Care license in Minnesota are as follows:\n\nRegulating Agency\n245D License: Regulated by the Minnesota Department of Human Services (DHS).\nHome Care License: Regulated by the Minnesota Department of Health (MDH).\n\nPrimary Focus\n245D License: Focuses on Home and Community-Based Services (HCBS) for individuals with disabilities and the elderly.\nHome Care License: Focuses on both medical and non-medical home care services.\n\nTarget Population\n245D License: Primarily serves Medicaid waiver recipients.\nHome Care License: Serves the general population needing home care.",
      sources: [
        "Minnesota Statute 245D.03 - Home and Community-Based Services Standards",
        "Minnesota Statute 144A.471 - Home Care License Requirements",
        "Minnesota DHS vs. MDH Regulatory Comparison Guide (2023)",
      ],
      icon: <MessageSquare className="h-5 w-5" />,
    },
    {
      id: 2,
      question: "I have a Home Care License, what should I look out for when going through a Site Survey/audit?",
      answer:
        "When preparing for a site survey or audit for your Home Care License in Minnesota, there are several key areas you should focus on to ensure compliance with regulations:\n\n1. Staff Records and Training\nPersonnel Files: Ensure that all personnel files are complete and include job descriptions, background checks, TB screenings, professional licenses or certifications, annual in-service training records, and orientation documentation for new staff.\nTracking System: Have a system in place to track staff compliance with education, licensure, and training requirements.\n\n2. Safety Devices\nSmoke Alarms and Fire Extinguishers: Make sure that all smoke alarms, fire extinguishers, and other safety devices are functional and compliant with regulations.\n\n3. Complaint Investigations\nBe prepared for complaint investigations, which are triggered by allegations of non-compliance. Ensure that you have processes in place to address and document any complaints.",
      sources: [
        "Minnesota Statute 144A.474 - Surveys and Inspections",
        "MKA Consulting - Home Care Audit Checklists",
        "Minnesota Department of Health - Home Care Provider Survey Guidelines",
      ],
      icon: <AlertCircle className="h-5 w-5" />,
    },
    {
      id: 3,
      question: "What policies and procedures do I need to develop or update to comply with 245D and other licensing requirements?",
      answer:
        "To comply with Minnesota Statutes Chapter 245D and other licensing requirements for Home and Community-Based Services (HCBS), you need to develop or update a comprehensive set of policies and procedures. Here are the key areas you should focus on:\n\n1. Service Termination and Suspension\nUpdate policies for temporary service suspension and service termination to ensure compliance with Section 245D.10, subdivision 4. This involves clearly defining the circumstances under which services can be suspended or terminated and the procedures to follow.\n\n2. Designated Roles\nIdentify and document the roles of the Designated Coordinator, Designated Manager, and other key personnel as required under Section 245D.081. This includes outlining their responsibilities and ensuring they are trained to fulfill their roles effectively.\n\n3. Service Recipient Rights\nEnsure that your policies protect and promote the rights of individuals receiving services, as outlined in Section 245D.04. This includes detailing staff responsibilities for upholding these rights and ensuring that service recipients are informed of their rights.\n\n4. Program Abuse Prevention Plan (PAPP)\nDevelop or update a Program Abuse Prevention Plan to prevent abuse of vulnerable adults or minors. This plan should include strategies for identifying, preventing, and responding to potential abuse situations.",
      sources: [
        "Minnesota Statute 245D.09 - Policy and Procedure Requirements",
        "Minnesota DHS - Home and Community-Based Services 245D (2016)",
        "Minnesota DHS - 245D Pre-application Worksheet",
      ],
      icon: <HelpCircle className="h-5 w-5" />,
    },
    {
      id: 4,
      question: "What are the fees for different types of Home Care licenses?",
      answer:
        "Home Care License fees in Minnesota vary based on the type of license:\n\nBasic Home Care License: $2,100\nComprehensive Home Care License: $4,200\n\nIn contrast, 245D License fees vary by service type and volume rather than having fixed rates. These fee structures reflect the different scopes and regulatory requirements for each license type.\n\nIt's important to note that fees are subject to change, and there may be additional costs associated with background studies, initial surveys, and other regulatory requirements. Always check with the Minnesota Department of Health (for Home Care licenses) or the Department of Human Services (for 245D licenses) for the most current fee information.",
      sources: [
        "Minnesota Department of Health - Home Care Provider Licensing Fee Schedule",
        "Minnesota Statute 144A.472 - Licensing Requirements",
        "Minnesota DHS - 245D License Fee Structure",
      ],
      icon: <Bookmark className="h-5 w-5" />,
    },
  ]

  const handleQuestionClick = (index: number) => {
    // Add animation class to card
    if (cardRef.current) {
      cardRef.current.classList.add("animate-card-change")
      setTimeout(() => {
        setActiveQuestionIndex(index)
        cardRef.current?.classList.remove("animate-card-change")
      }, 300)
    } else {
      setActiveQuestionIndex(index)
    }
  }

  const activeQuestion = questions[activeQuestionIndex]

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white dark:from-[#0F172A] dark:to-[#1E293B] overflow-hidden">
      {/* Curved section top divider */}
      <div className="absolute top-0 inset-x-0 h-16">
        <svg className="absolute inset-0 h-full w-full text-white dark:text-[#0F172A]" preserveAspectRatio="none" viewBox="0 0 1440 80" fill="currentColor">
          <path d="M0,0V80H1440V0C1440,0,1320,60,720,60C120,60,0,0,0,0Z" />
        </svg>
      </div>
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute top-1/4 -left-20 w-60 h-60 bg-primary/10 dark:bg-primary/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-0 w-80 h-80 bg-primary-light/10 dark:bg-primary-light/20 rounded-full blur-3xl"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-gray-500/[0.03] dark:bg-grid-gray-400/[0.05] bg-[length:30px_30px]"></div>
      </div>

      <div className="container px-4 md:px-8 lg:px-12 mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
          <div className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light mb-4 backdrop-blur-sm">
            SOLVE REAL COMPLIANCE PROBLEMS
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground dark:text-white mb-6 tracking-tight">
            Answers to <span className="text-primary dark:text-primary-light">Tough Questions</span>
          </h2>
          <p className="text-xl text-muted-foreground dark:text-gray-300 max-w-2xl mx-auto">
            CareAdvisor provides clear guidance on your most complex Minnesota compliance challenges, with references to official government sources.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-2">
            <div className="relative" ref={cardRef}>
              {/* Card glow effect */}
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-r from-primary/20 to-primary-light/20 dark:from-primary/30 dark:to-primary-light/30 opacity-70 blur-xl"></div>
              
              <div className="relative bg-white dark:bg-[#1E293B] rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800/50 overflow-hidden">
                <div className="p-6 border-b border-gray-100 dark:border-gray-800/50 flex items-center justify-between">
                  <div className="flex items-center">
                    <Search className="h-5 w-5 text-primary dark:text-primary-light mr-2" />
                    <h3 className="text-xl font-semibold text-foreground dark:text-white">Ask CareAdvisor</h3>
                  </div>
                  <div className="bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light text-sm font-medium py-1 px-3 rounded-full">
                    AI Powered
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="h-8 w-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light mt-1 flex-shrink-0">
                      {activeQuestion.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-foreground dark:text-white mb-4">
                        {activeQuestion.question}
                      </h4>
                      <div className="text-muted-foreground dark:text-gray-300">
                        {activeQuestion.answer.split("\n\n").map((paragraph, idx) => (
                          <p key={idx} className="mb-4">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 bg-gray-50 dark:bg-[#131E31] p-5 rounded-xl border border-gray-100 dark:border-gray-800/50">
                    <div className="flex items-center mb-3">
                      <ExternalLink className="h-4 w-4 text-primary dark:text-primary-light mr-2" />
                      <p className="text-sm font-medium text-foreground dark:text-white">Sources:</p>
                    </div>
                    <ul className="text-sm text-muted-foreground dark:text-gray-400 space-y-2">
                      {activeQuestion.sources.map((source, i) => (
                        <li key={i} className="flex items-start">
                          <span className="mr-2 text-primary dark:text-primary-light">•</span>
                          <span>{source}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="relative">
              {/* Subtle glow effect */}
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-r from-primary/5 to-primary-light/5 dark:from-primary/10 dark:to-primary-light/10 opacity-80 blur-lg"></div>
              
              <div className="relative bg-white dark:bg-[#1E293B] rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800/50 p-6">
                <h3 className="text-xl font-bold text-foreground dark:text-white mb-4">Popular Questions</h3>
                <div className="space-y-3">
                  {questions.map((question, index) => (
                    <button
                      key={question.id}
                      onClick={() => handleQuestionClick(index)}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                        index === activeQuestionIndex
                          ? "bg-gradient-to-r from-primary/10 to-primary-light/10 dark:from-primary/20 dark:to-primary-light/20 border-l-4 border-primary dark:border-primary-light transform -translate-x-1"
                          : "bg-gray-50 dark:bg-[#131E31] hover:bg-gray-100 dark:hover:bg-gray-800/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`${
                            index === activeQuestionIndex
                              ? "text-primary dark:text-primary-light"
                              : "text-muted-foreground dark:text-gray-400"
                          }`}
                        >
                          {question.icon}
                        </div>
                        <span
                          className={`font-medium ${
                            index === activeQuestionIndex
                              ? "text-foreground dark:text-white"
                              : "text-muted-foreground dark:text-gray-300"
                          }`}
                        >
                          {question.question}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-6">
                  <Button
                    variant="outline"
                    className="w-full bg-white dark:bg-[#131E31] text-primary dark:text-primary-light border-primary/30 dark:border-primary-light/30 hover:bg-primary/5 dark:hover:bg-primary/10 flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-md"
                  >
                    View All Questions <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Curved section bottom divider */}
      <div className="absolute bottom-0 inset-x-0 h-16">
        <svg className="absolute inset-0 h-full w-full text-gray-50 dark:text-[#1E293B]" preserveAspectRatio="none" viewBox="0 0 1440 80" fill="currentColor">
          <path d="M1440,80V0H0V80C0,80,120,20,720,20C1320,20,1440,80,1440,80Z" />
        </svg>
      </div>
    </section>
  )
}

